import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import { crossPlatformStorage } from './storage';

/**
 * Application Settings Store
 *
 * User preferences that persist across sessions.
 */

type Theme = 'light' | 'dark' | 'system';

interface SettingsState {
  /** Color theme preference */
  theme: Theme;

  /** Whether haptic feedback is enabled (mobile only) */
  hapticFeedback: boolean;

  /** Whether sidebar is hidden for fullscreen dashboard mode */
  sidebarHidden: boolean;

  /** Whether to hide the system status bar (mobile only) */
  hideStatusBar: boolean;

  /** Set theme */
  setTheme: (theme: Theme) => void;

  /** Toggle haptic feedback */
  setHapticFeedback: (enabled: boolean) => void;

  /** Toggle sidebar visibility */
  setSidebarHidden: (hidden: boolean) => void;

  /** Toggle sidebar */
  toggleSidebar: () => void;

  /** Set status bar visibility */
  setHideStatusBar: (hidden: boolean) => void;
}

export const useSettingsStore = create<SettingsState>()(
  persist(
    (set) => ({
      theme: 'system',
      hapticFeedback: true,
      sidebarHidden: false,
      hideStatusBar: false,

      setTheme: (theme) => set({ theme }),

      setHapticFeedback: (hapticFeedback) => set({ hapticFeedback }),

      setSidebarHidden: (sidebarHidden) => set({ sidebarHidden }),

      toggleSidebar: () => set((state) => ({ sidebarHidden: !state.sidebarHidden })),

      setHideStatusBar: (hideStatusBar) => set({ hideStatusBar }),
    }),
    {
      name: 'smarthome-settings',
      storage: crossPlatformStorage,
    }
  )
);
